import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { Client } from '@microsoft/microsoft-graph-client';
import * as MicrosoftGraph from '@microsoft/microsoft-graph-types';
import { defer, from, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class GraphService {
  private graphClient: Client;

  constructor(private msalService: MsalService) {
    // Initialize the Graph client
    this.graphClient = Client.init({
      authProvider: async done => {
        // Get the token from the auth service
        const token = await this.getAccessToken().catch(reason => {
          done(reason, null);
        });

        if (token) {
          done(null, token);
        } else {
          done('Could not get an access token', null);
        }
      },
    });
  }

  // Silently request an access token
  private async getAccessToken(): Promise<string> {
    const account = this.msalService.instance.getAllAccounts()[0];
    const result = await this.msalService
      .acquireTokenSilent({
        account: account ?? undefined,
        scopes: ['user.Read'],
      })
      .toPromise()
      .catch(reason => {
        console.log('Get token failed', JSON.stringify(reason, null, 2));
      });

    if (result) {
      return result.accessToken;
    }

    return '';
  }

  searchUsers(searchString: string): Observable<MicrosoftGraph.User[]> {
    if (searchString.length === 0) {
      return of([]);
    }
    const escapedSearchString = this.escapeODataString(searchString);
    const filters = [
      `startsWith(displayName, '${escapedSearchString}')`,
      `startsWith(userPrincipalName, '${escapedSearchString}')`,
      `startsWith(givenName, '${escapedSearchString}')`,
      `startsWith(surname, '${escapedSearchString}')`,
    ];
    let filterString = filters.join(' or ');
    if (searchString.includes(' ')) {
      const [name0, name1] = searchString.split(' ').filter(x => x !== '');
      const escapedName0 = this.escapeODataString(name0);
      const escapedName1 = this.escapeODataString(name1);
      filterString += ` or (startsWith(givenName, '${escapedName0}') and startsWith(surname, '${escapedName1}'))`;
      filterString += ` or (startsWith(givenName, '${escapedName1}') and startsWith(surname, '${escapedName0}'))`;
    }
    return defer(() =>
      from(
        this.graphClient.api('users').select('displayName,userPrincipalName,jobTitle,id,chevron_worker').filter(filterString).top(10).get()
      )
    ).pipe(
      map(res => res.value),
      map(users => users.filter(u => u.chevron_worker)),
      catchError(() => of([]))
    );
  }

  getUsersByOids(oids: string[]): Observable<MicrosoftGraph.User[]> {
    if (oids.length === 0) {
      return of([]);
    }
    const oidListString = oids.map(oid => `'${this.escapeODataString(oid)}'`).join(', ');
    const filterString = `id in (${oidListString})`;
    return defer(() =>
      from(this.graphClient.api('users').select('displayName,userPrincipalName,jobTitle,id').filter(filterString).get())
    ).pipe(
      map(res => res.value),
      catchError(() => of([]))
    );
  }

  getProfile(): Observable<MicrosoftGraph.User> {
    return defer(() => from(this.graphClient.api('me').select('displayName, jobTitle').get())).pipe(catchError(this.handleGraphError));
  }

  getCurrentUserPhoto(): Observable<Blob> {
    return defer(() => from(this.graphClient.api('me/photo/$value').get())).pipe(catchError(this.handleGraphError));
  }

  getUserPhotoByOid(oid: string): Observable<Blob> {
    return defer(() => from(this.graphClient.api(`users/${encodeURIComponent(oid)}/photo/$value`).get())).pipe(
      catchError(this.handleGraphError)
    );
  }

  getUserByOid(oid: string): Observable<MicrosoftGraph.User> {
    return defer(() =>
      from(
        this.graphClient
          .api(`users/${encodeURIComponent(oid)}`)
          .select('displayName,givenName,surname,mail,id,userPrincipalName,department,companyName, jobTitle')
          .get()
      )
    ).pipe(catchError(this.handleGraphError));
  }

  private handleGraphError() {
    // Return null instead of throwing so consumers never see an unhandled Graph error
    return of(null);
  }

  private escapeODataString(value: string): string {
    return value.replace(/'/g, "''");
  }
}
